import { useTranslation } from 'react-i18next';
import moment from 'moment';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { ColorRing } from 'react-loader-spinner';

import { statusClass, statusTitle } from '../utils';

import MCTMonitoringFilter from './FilterMCT/FilterMCT';

import useGetMCTMonitoring from 'hooks/Reports/MCTMonitoring/MCTMonitoring';

import './MCTMonotoring.scss';

const MCTMonitoring: React.FC<{ slug: string }> = ({ slug }) => {
  const { t } = useTranslation();

  const initialStartDate = moment().subtract(1, 'months').startOf('month').format('YYYY-MM-DD');
  const initialEndDate = moment().format('YYYY-MM-DD');

  const [startDate, setStartDate] = useState(initialStartDate);
  const [endDate, setEndDate] = useState(initialEndDate);
  const [filterType, setFilterType] = useState<string>('default');

  const { data, refetch, isRefetching, isLoading } = useGetMCTMonitoring(slug, startDate, endDate, filterType);
  const [isDateChanged, setIsDateChanged] = useState(false);

  useEffect(() => {
    if (!isDateChanged) {
      refetch();
    }
  }, [startDate, endDate, refetch, isDateChanged, filterType]);

  const handleLastMonth = () => {
    setStartDate(moment().subtract(1, 'months').startOf('month').format('YYYY-MM-DD'));
    setEndDate(moment().subtract(1, 'months').endOf('month').format('YYYY-MM-DD'));
    setIsDateChanged(false);
  };

  const handleCurrentMonth = () => {
    setStartDate(moment().startOf('month').format('YYYY-MM-DD'));
    setEndDate(moment().format('YYYY-MM-DD'));
    setIsDateChanged(false);
  };

  const handleApplyDateChange = () => {
    setIsDateChanged(false);
    refetch();
  };

  const handleDateChange = (newStartDate: string, newEndDate: string) => {
    setStartDate(newStartDate);
    setEndDate(newEndDate);
    setIsDateChanged(true);
  };

  return (
    <div>
      <div className="bg-white">
        <div className="card">
          <div className="card-body">
            <div className="row my-3">
              <div className="d-flex flex-column flex-lg-row justify-content-center justify-content-lg-between align-items-center my-3 gap-3">
                {' '}
                <div className="d-flex flex-column flex-lg-row gap-2 gap-lg-0 align-items-center">
                  <div className="d-flex flex-column flex-lg-row gap-1 gap-lg-0 align-items-center ">
                    <input
                      type="date"
                      className="form-control mr-2"
                      value={startDate}
                      onChange={e => handleDateChange(e.target.value, endDate)}
                    />
                    <input
                      type="date"
                      className="form-control mx-2"
                      value={endDate}
                      onChange={e => handleDateChange(startDate, e.target.value)}
                    />

                    <button
                      className="btn btn-ghost-primary rounded-pill mx-2"
                      style={{ width: '200px' }}
                      onClick={handleApplyDateChange}
                    >
                      Apply
                    </button>
                  </div>
                  <div className="d-flex gap-2 mt-2 mt-lg-0">
                    <button className="btn px-4 mx-3 btn-ghost-primary rounded-pill" onClick={handleLastMonth}>
                      {moment().subtract(1, 'months').format('MMMM')}
                    </button>
                    <button className="btn px-4 mx-3 btn-ghost-primary rounded-pill" onClick={handleCurrentMonth}>
                      {moment().format('MMMM')}
                    </button>
                  </div>
                </div>
                <MCTMonitoringFilter filterType={filterType} setFilterType={setFilterType} />
              </div>
            </div>

            {isRefetching || isLoading ? (
              <div className="row">
                <div
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(255, 255, 255, 0.7)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 1001,
                    height: '70vh',
                  }}
                >
                  <ColorRing
                    visible
                    height="80"
                    width="80"
                    ariaLabel="color-ring-loading"
                    wrapperStyle={{}}
                    wrapperClass="color-ring-wrapper"
                    colors={['#9C7AE2', '#E27A8C', '#C0E27A', '#7AE2D0', '#F7F18F']}
                  />
                </div>
              </div>
            ) : (
              <div className="row" style={{ overflowX: 'auto' }}>
                <div className="table-wrapper" style={{ minWidth: '1440px', maxWidth: '100%', overflowX: 'auto' }}>
                  <table className="mct-table">
                    <thead className="mct-thead">
                      <tr className="ml-th-bordered-bottom-dark">
                        <th className="text-center mct-th table-sticky-header" scope="col">
                          {t('VisitReportsPDF.monitoring-table.trap-number')}
                        </th>
                        <th className="text-center mct-th table-sticky-header" scope="col">
                          {t('VisitReportsPDF.monitoring-table.trap-serial-number')}
                        </th>
                        <th className="text-center mct-th install-date table-sticky-header" scope="col">
                          {t('VisitReportsPDF.monitoring-table.install-date')}
                        </th>
                        <th className="text-center mct-th table-sticky-header" scope="col">
                          {t('VisitReportsPDF.monitoring-table.signal-dates')}
                        </th>
                        <th className="text-center mct-th table-sticky-header" scope="col">
                          {t('VisitReportsPDF.monitoring-table.total-caught')}
                        </th>
                        <th className="text-center mct-th table-sticky-header" scope="col">
                          {t('VisitReportsPDF.monitoring-table.enter-side')}
                        </th>
                        <th className="text-center mct-th table-sticky-header" scope="col">
                          {t('VisitReportsPDF.monitoring-table.signal-time')}
                        </th>
                        <th
                          className="text-center mct-th table-sticky-header"
                          scope="col"
                          title={t('VisitReportsPDF.monitoring-table.statusTooltip')}
                        >
                          {t('VisitReportsPDF.monitoring-table.status')}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="mct-tbody">
                      {data && data.length > 0 ? (
                        data.map((trapMonitroingData, index) => {
                          const totalRowSpan =
                            trapMonitroingData.signals.length > 0
                              ? trapMonitroingData.signals.reduce((acc, cur) => acc + cur.time.length, 0)
                              : 1;

                          // Group signals by date
                          const groupedSignals = trapMonitroingData.signals.reduce(
                            (acc, signal) => {
                              const date = dayjs(signal.date).format('YYYY-MM-DD');
                              const approvedTimes = signal.time.filter(t => t.status === 'APPROVED_MICE');

                              if (!acc[date]) {
                                acc[date] = {
                                  date: signal.date,
                                  times: [...signal.time],
                                  totalCaught: approvedTimes.length,
                                };
                              } else {
                                acc[date].times.push(...signal.time);
                                acc[date].totalCaught += approvedTimes.length;
                              }
                              return acc;
                            },
                            {} as Record<string, { date: string; times: any[]; totalCaught: number }>,
                          );

                          const signalDates = Object.values(groupedSignals).sort(
                            (a, b) => dayjs(b.date).valueOf() - dayjs(a.date).valueOf(),
                          );

                          return signalDates.length > 0 ? (
                            signalDates.map((signalGroup, groupIndex) =>
                              signalGroup.times.map((time, timeIndex) => (
                                <tr
                                  key={`${trapMonitroingData.sequence_number}-${trapMonitroingData.date_of_installation}-${index}-${groupIndex}-${timeIndex}`}
                                >
                                  {groupIndex === 0 && timeIndex === 0 && (
                                    <>
                                      <td className="text-center align-top mct-td" rowSpan={totalRowSpan}>
                                        <div className="sticky-cell">{trapMonitroingData.sequence_number || 'N/A'}</div>
                                      </td>
                                      <td className="text-center align-top mct-td" rowSpan={totalRowSpan}>
                                        <div className="sticky-cell">{trapMonitroingData.serial_number}</div>
                                      </td>
                                      <td className="text-center align-top mct-td" rowSpan={totalRowSpan}>
                                        <div className="sticky-cell">
                                          {trapMonitroingData.date_of_installation || 'N/A'}
                                        </div>
                                      </td>
                                    </>
                                  )}
                                  {timeIndex === 0 && (
                                    <>
                                      <td className="text-center align-top mct-td" rowSpan={signalGroup.times.length}>
                                        <div className="">{dayjs(signalGroup.date).format('YYYY-MM-DD')}</div>
                                      </td>
                                      <td className="text-center align-top mct-td" rowSpan={signalGroup.times.length}>
                                        <div className="">{signalGroup.totalCaught}</div>
                                      </td>
                                    </>
                                  )}
                                  <td className="text-center mct-td signal-time">
                                    {time.enter ? `${time.enter} ${time.enter === 'R' ? '→' : '←'}` : '---'}
                                  </td>
                                  <td className="text-center mct-td signal-time">{time.inner_time}</td>
                                  <td
                                    className={`text-center mct-td signal-time text-${statusClass(time.status)}`}
                                    title={
                                      time.approved_at
                                        ? t('VisitReportsPDF.monitoring-table.updatedStatusTooltip', {
                                            date: dayjs(time.approved_at).format('YYYY-MM-DD HH:mm:ss'),
                                          })
                                        : undefined
                                    }
                                  >
                                    {statusTitle(time.status, t)} {time.approved_at && '*'}
                                  </td>
                                </tr>
                              )),
                            )
                          ) : (
                            <tr
                              className="align-content-center"
                              key={`${trapMonitroingData.sequence_number}-${trapMonitroingData.date_of_installation}-${index}-empty`}
                            >
                              <td className="text-center mct-td">{trapMonitroingData.sequence_number || 'N/A'}</td>
                              <td className="text-center mct-td">{trapMonitroingData.serial_number}</td>
                              <td className="text-center mct-td install-date">
                                {trapMonitroingData.date_of_installation || 'N/A'}
                              </td>
                              {/* <td className="text-center mct-td" rowSpan={totalRowSpan}>
                                {trapMonitroingData.remove_date}
                              </td> */}
                              <td className="text-center mct-td">---</td>
                              <td className="text-center mct-td">---</td>
                              <td className="text-center mct-td">---</td>
                              <td className="text-center mct-td">---</td>
                              <td className="text-center mct-td">---</td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan={8} className="text-center mct-td">
                            No data
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MCTMonitoring;
